/**
 * Client for the `analytics-aggregate` edge function.
 *
 * The aggregation itself runs server-side (see the function's header for why),
 * so this is a single POST that returns a dataset already shaped like the
 * analytics UI's demo dataset.
 */
import { TOKEN_KEY } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain } from "@/lib/powtoon-oauth";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";
import { createLogger } from "@/lib/frontend-logger";
import type { Avatar, Session } from "./demo-data";
import type { RangeKey } from "./range";

const logger = createLogger("analytics.live");
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/analytics-aggregate`;

/** A live session row carries the ids needed to fetch its real transcript. */
export type LiveSession = Session & {
  scenarioId: string;
  transcriptObjectId: string | null;
};

export type LiveAnalytics = {
  /** Whether this scope has any recorded sessions at all (ignores the range). */
  hasAny: boolean;
  scannedScenarios: number;
  avatars: Avatar[];
  sessions: LiveSession[];
  weeks: string[];
  minutes: number[];
  learners: number[];
};

export async function fetchLiveAnalytics(range: RangeKey): Promise<LiveAnalytics> {
  const token = typeof window === "undefined" ? null : window.sessionStorage.getItem(TOKEN_KEY);
  if (!token) throw new Error("Not signed in");
  const org = getOrgDomain();
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();

  const res = await fetch(appendOp(ENDPOINT, "aggregate-analytics"), {
    method: "POST",
    headers: {
      "content-type": "application/json",
      apikey: SUPABASE_KEY,
      authorization: `Bearer ${SUPABASE_KEY}`,
      "x-powtoon-token": token,
      ...(org ? { "x-powtoon-org": org } : {}),
      [CORRELATION_HEADER]: correlationId,
    },
    body: JSON.stringify({ range }),
  });

  const durationMs = Math.round(performance.now() - startedAt);
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    logger.error("live analytics fetch failed", {
      range,
      status: res.status,
      correlationId,
      durationMs,
      snippet: body.slice(0, 300),
    });
    throw new Error(`Analytics unavailable (${res.status})`);
  }

  const data = (await res.json()) as LiveAnalytics;
  logger.info("live analytics fetch ok", {
    range,
    correlationId,
    durationMs,
    scenarios: data.scannedScenarios,
    sessions: data.sessions?.length ?? 0,
    cache: res.headers.get("x-cache") ?? undefined,
  });
  return data;
}
